import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { DataTableModel, getApplicationListRespModel } from "../Models/tableDetailsModel";
import { rowCountPerPage, rowCountPerPageOptions } from "../constants/rowsPerPageOptions";
import {
    IGetEscalationDialogInfo,
    FilterModel,
    IEscalation,
    IEscalationInfoRes,
    IGetAnalysisResultValue,
    SortInfoModel,
    ApplicationEventHistoryFE
} from "../Models/applicationModels";
import { useSearchParams } from "react-router-dom";
import {
    InsightsDateRange,
    InsightsUiFilters,
} from "../Pages/InsightsPage/components/insightsHeader/insightsHeader.models";
import { DEFAULT_INSIGHTS_DATE_RANGE, DEFAULT_INSIGHTS_UI_FILTERS } from "../Pages/InsightsPage/components/insightsHeader/insightsHeader.constants";


interface InsightsContextPayload {
  applicationListColumns: DataTableModel[] | null;
  setApplicationListColumns: React.Dispatch<React.SetStateAction<DataTableModel[] | null>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalRowCount: number;
  setTotalRowCount: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: rowCountPerPage;
  setRowsPerPage: React.Dispatch<React.SetStateAction<rowCountPerPage>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  sortInfo: SortInfoModel | null;
  setSortInfo: React.Dispatch<React.SetStateAction<SortInfoModel | null>>;
  sortInfoRef: React.MutableRefObject<SortInfoModel | null>;
  insightsUiFilters: InsightsUiFilters;
  setInsightsUiFilters: React.Dispatch<React.SetStateAction<InsightsUiFilters>>;
  visibleColumns: string[] | null;
  setVisibleColumns: React.Dispatch<React.SetStateAction<string[] | null>>;
  currentApplicationAnalysisResult: IGetAnalysisResultValue | null;
  setCurrentApplicationAnalysisResult: React.Dispatch<React.SetStateAction<IGetAnalysisResultValue | null>>;
  searchTextRef: React.MutableRefObject<string>;
  rowPerPageRef: React.MutableRefObject<number>;
  filterInfoRef: React.MutableRefObject<FilterModel[]>;
  dateRangeRef: React.MutableRefObject<InsightsDateRange>;
  tableBodyRef: React.MutableRefObject<HTMLTableSectionElement | null>;
  tableColumnInfo: React.MutableRefObject<getApplicationListRespModel | null>;
  tableWidth: React.MutableRefObject<number>;
  filterInfoStatic: React.MutableRefObject<FilterModel[] | null>;
  escalationInfo: IEscalationInfoRes[];
  setEscalationInfo: React.Dispatch<React.SetStateAction<IEscalationInfoRes[]>>;
  escalationDialogInfo: IGetEscalationDialogInfo | null;
  setEscalationDialogInfo: React.Dispatch<React.SetStateAction<IGetEscalationDialogInfo | null>>;
  detailsPageEscalations: IEscalation[] | null;
  setDetailsPageEscalations: React.Dispatch<React.SetStateAction<IEscalation[] | null>>;
  detailsPageApplicationDetails: getApplicationListRespModel | null;
  setDetailsPageApplicationDetails: React.Dispatch<React.SetStateAction<getApplicationListRespModel | null>>;
  detailsPageOpportunityId: string | null;
  setDetailsPageOpportunityId: React.Dispatch<React.SetStateAction<string | null>>;
  associatedEscalation: string[];
  setAssociatedEscalation: React.Dispatch<React.SetStateAction<string[]>>;
  isEventHistoryModalOpen: boolean;
  setIsEventHistoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEventHistoryLoading: boolean;
  setIsEventHistoryLoading: React.Dispatch<React.SetStateAction<boolean>>;
  eventHistoryResponse: ApplicationEventHistoryFE | null;
  setEventHistoryResponse: React.Dispatch<React.SetStateAction<ApplicationEventHistoryFE | null>>;
  eventHistoryRequestData: {
    opportunityUid: string;
    firstName: string;
    lastName: string;
  } | null;
  setEventHistoryRequestData: React.Dispatch<React.SetStateAction<{
    opportunityUid: string;
    firstName: string;
    lastName: string;
  } | null>>;
}

const InsightsContext = createContext<InsightsContextPayload | null>(null);

const DEFAULT_PAGE = 1;
const DEFAULT_ROWS = 20;

export const InsightsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [searchParams, setSearchParams] = useSearchParams();

    const getPageFromParams = () => {
        const pageParam = parseInt(searchParams.get('page') || '');
        return Number.isNaN(pageParam) || pageParam < 1 ? DEFAULT_PAGE : pageParam;
    };

    const getRowsFromParams = (): rowCountPerPage => {
        const rowsParam = parseInt(searchParams.get('rows') || '');
        return rowCountPerPageOptions.includes(rowsParam as rowCountPerPage)
            ? (rowsParam as rowCountPerPage)
            : DEFAULT_ROWS;
    };


  const [applicationListColumns, setApplicationListColumns] = useState<DataTableModel[] | null>(null);
  const [page, setPage] = useState(getPageFromParams);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<rowCountPerPage>(getRowsFromParams);
  const [searchText, setSearchText] = useState<string>('');
  const [sortInfo, setSortInfo] = useState<SortInfoModel | null>(null);
  const [insightsUiFilters, setInsightsUiFilters] = useState<InsightsUiFilters>(DEFAULT_INSIGHTS_UI_FILTERS);
  const [visibleColumns, setVisibleColumns] = useState<string[] | null>(null);
  const [currentApplicationAnalysisResult, setCurrentApplicationAnalysisResult] = useState<IGetAnalysisResultValue | null>(null);
  const [escalationInfo, setEscalationInfo] = useState<IEscalationInfoRes[]>([]);
  const [escalationDialogInfo, setEscalationDialogInfo] = useState<IGetEscalationDialogInfo | null>(null)
  const [detailsPageEscalations, setDetailsPageEscalations] = useState<IEscalation[] | null>(null);
  const [detailsPageApplicationDetails, setDetailsPageApplicationDetails] = useState<getApplicationListRespModel | null>(null);
  const [detailsPageOpportunityId, setDetailsPageOpportunityId] = useState<string | null>(null);
  const [associatedEscalation, setAssociatedEscalation] = useState<string[]>([]);
  const [isEventHistoryModalOpen, setIsEventHistoryModalOpen] = useState(false);
  const [isEventHistoryLoading, setIsEventHistoryLoading] = useState(false);
  const [eventHistoryResponse, setEventHistoryResponse] = useState<ApplicationEventHistoryFE | null>(null);
  const [eventHistoryRequestData, setEventHistoryRequestData] = useState<{
    opportunityUid: string;
    firstName: string;
    lastName: string;
  } | null>(null);

  // Refs
  const filterInfoStatic = useRef<FilterModel[] | null>(null);
  const searchTextRef = useRef(searchText);
  const rowPerPageRef = useRef(rowsPerPage);
  const filterInfoRef = useRef<FilterModel[]>([]);
  const dateRangeRef = useRef<InsightsDateRange>(DEFAULT_INSIGHTS_DATE_RANGE);
  const sortInfoRef = useRef<SortInfoModel | null>(sortInfo);
  // Refs keep latest search/filter/page values for sort and other table handlers
  // that are wired through column definitions and can close over stale state.
  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);
  const tableColumnInfo = useRef<getApplicationListRespModel | null>(null);
  const tableWidth = useRef<number>(0);


    useEffect(() => {
        if (window.location.pathname === '/insights') {
            const current = Object.fromEntries(searchParams.entries());

            const updated = {
                ...current,
                page: page.toString(),
                rows: rowsPerPage.toString(),
            };

            setSearchParams(updated, { replace: true });
        }
    }, [page, rowsPerPage]);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  useEffect(() => {
    rowPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);

  useEffect(() => {
    sortInfoRef.current = sortInfo;
  }, [sortInfo]);

  return (
    <InsightsContext.Provider
      value={{
        applicationListColumns,
        setApplicationListColumns,
        page,
        setPage,
        totalRowCount,
        setTotalRowCount,
        rowsPerPage,
        setRowsPerPage,
        searchText,
        setSearchText,
        sortInfo,
        setSortInfo,
        sortInfoRef,
        insightsUiFilters,
        setInsightsUiFilters,
        visibleColumns,
        setVisibleColumns,
        currentApplicationAnalysisResult,
        setCurrentApplicationAnalysisResult,
        filterInfoStatic,
        searchTextRef,
        rowPerPageRef,
        filterInfoRef,
        dateRangeRef,
        tableBodyRef,
        tableColumnInfo,
        tableWidth,
        escalationInfo,
        setEscalationInfo,
        escalationDialogInfo,
        setEscalationDialogInfo,
        detailsPageEscalations,
        setDetailsPageEscalations,
        detailsPageApplicationDetails,
        setDetailsPageApplicationDetails,
        detailsPageOpportunityId,
        setDetailsPageOpportunityId,
        associatedEscalation,
        setAssociatedEscalation,
        isEventHistoryModalOpen,
        setIsEventHistoryModalOpen,
        isEventHistoryLoading,
        setIsEventHistoryLoading,
        eventHistoryResponse,
        setEventHistoryResponse,
        eventHistoryRequestData,
        setEventHistoryRequestData
      }}
    >
      {children}
    </InsightsContext.Provider>
  );
};

// Custom Hook
export const useInsights = (): InsightsContextPayload => {
  const context = useContext(InsightsContext);
  if (!context) {
    throw new Error("useInsights must be used within an InsightsProvider");
  }
  return context;
};

export default InsightsContext;